/** Same-origin API boundary with safe error-envelope and request correlation handling. */
import { apiErrorEnvelopeSchema } from "@loomua/contracts";

type Schema<T> = { parse(value: unknown): T };
type Fetcher = typeof fetch;

export class ApiClientError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly requestId?: string,
    readonly details: Record<string, unknown> = {}
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

export function createApiClient(
  options: {
    fetcher?: Fetcher;
    getAccessToken?: () => Promise<string | null>;
  } = {}
) {
  const fetcher = options.fetcher ?? fetch;

  return {
    async request<T>(
      path: `/api/${string}`,
      request: {
        schema: Schema<T>;
        method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
        body?: unknown;
        formData?: FormData;
        signal?: AbortSignal;
      }
    ): Promise<T> {
      const accessToken = await options.getAccessToken?.();
      const headers: Record<string, string> = { Accept: "application/json" };
      if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
      if (request.body !== undefined)
        headers["Content-Type"] = "application/json";

      let response: Response;
      try {
        response = await fetcher(path, {
          method: request.method ?? "GET",
          headers,
          body: request.formData
            ? request.formData
            : request.body === undefined
              ? undefined
              : JSON.stringify(request.body),
          signal: request.signal
        });
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          throw error;
        }
        throw new ApiClientError(
          "Check your connection and try again.",
          0,
          "NETWORK_FAILURE"
        );
      }

      const payload: unknown = await response
        .json()
        .then((value: unknown) => value)
        .catch(() => undefined);
      if (!response.ok) {
        const parsed = apiErrorEnvelopeSchema.safeParse(payload);
        if (parsed.success) {
          throw new ApiClientError(
            parsed.data.error.message,
            response.status,
            parsed.data.error.code,
            parsed.data.error.requestId,
            parsed.data.error.details
          );
        }
        throw new ApiClientError(
          "The request could not be completed.",
          response.status,
          "UNEXPECTED_RESPONSE",
          response.headers.get("x-request-id") ?? undefined
        );
      }
      try {
        return request.schema.parse(payload);
      } catch {
        throw new ApiClientError(
          "The server returned an unexpected response.",
          response.status,
          "UNEXPECTED_RESPONSE",
          response.headers.get("x-request-id") ?? undefined
        );
      }
    }
  };
}
